import {Component} from 'react'
import PropTypes from 'prop-types'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorIcon from '@mui/icons-material/Error'

import TfaWizardStepFrame from './TfaWizardStepFrame'
import {Text} from '../../../../../dashboard/src/components/blocks/Texts'


class ConfirmCodeForm extends Component {
  constructor() {
    super()

    this.codeInputRefs = []
    this.state = {
      code: Array(6).fill(''),
      localError: '',
      loading: false
    }

    this.getEnteredCode = this.getEnteredCode.bind(this)
    this.handleCodeChange = this.handleCodeChange.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handlePaste = this.handlePaste.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.setInputRef = this.setInputRef.bind(this)
  }

  static getDerivedStateFromProps(props, state) {
    if ((props.error || props.phoneVerified) && state.loading)
      return {loading: false}

    return null
  }

  getEnteredCode() { return this.state.code.join('') }

  getMaskedNumber() {
    const phoneNumber = (this.props.phoneNumber && this.props.phoneNumber.get('national-number')) || ''
    const digits = phoneNumber.replace(/\D/g, '')
    const lastFourDigits = digits.slice(-4)

    return lastFourDigits ? `***-***-${lastFourDigits}` : phoneNumber
  }

  setInputRef(index, element) {
    this.codeInputRefs[index] = element
  }

  handlePaste(event) {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)

    if (!digits)
      return

    event.preventDefault()

    const nextCode = Array(6).fill('').map((value, index) => digits[index] || '')

    this.setState({code: nextCode, localError: ''}, () => {
      const focusIndex = Math.min(digits.length, 6) - 1

      if (this.codeInputRefs[focusIndex])
        this.codeInputRefs[focusIndex].focus()
    })
  }

  handleCodeChange(index, event) {
    const digits = event.target.value.replace(/\D/g, '')
    const nextCode = [...this.state.code]

    if (digits.length > 1) {
      digits.slice(0, 6 - index).split('').forEach((digit, offset) => {
        nextCode[index + offset] = digit
      })
    } else {
      nextCode[index] = digits
    }

    this.setState({code: nextCode, localError: ''}, () => {
      const nextIndex = digits.length > 1 ? index + digits.length - 1 : index + 1

      if (digits && nextIndex < this.codeInputRefs.length && this.codeInputRefs[nextIndex])
        this.codeInputRefs[nextIndex].focus()
    })
  }

  handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !this.state.code[index] && index > 0 && this.codeInputRefs[index - 1])
      this.codeInputRefs[index - 1].focus()
  }

  handleSubmit(event) {
    if (event) event.preventDefault()

    if (this.getEnteredCode().length !== 6) {
      this.setState({localError: 'Enter the 6-digit verification code.'})
      return
    }

    this.setState({loading: true})

    if (this.props.enrollmentMode === 'phone')
      this.props.confirmCode({confirmCode: this.getEnteredCode()})
    else
      this.props.verifyEnrollment({code: this.getEnteredCode()})
  }

  subtitleText() {
    return this.props.enrollmentMode === 'app' ? "Enter the 6-digit code from your authenticator app to confirm it's working." : `Enter the 6-digit code we sent to ${this.getMaskedNumber()}.`
  }

  primaryActionLabel() {
    if (this.props.enrollmentMode === 'phone')
      return this.props.phoneVerified ? 'Verified' : 'Submit'
    else
      return 'Continue'
  }

  render() {
    const error = this.props.error || this.state.localError

    return (
      <TfaWizardStepFrame
        currentStep={3}
        onPrimaryAction={this.handleSubmit}
        onSecondaryAction={this.props.onBack}
        primaryActionLabel={this.primaryActionLabel()}
        primaryDisabled={this.getEnteredCode().length !== 6 || this.props.phoneVerified}
        primaryLoading={this.state.loading}
        secondaryActionLabel='Back'
        subtitle={this.subtitleText()}
        title='Verify your setup'>
        <form className='tfa-confirm-form' onPaste={this.handlePaste} onSubmit={this.handleSubmit}>
          <div className='tfa-confirm-code-panel'>
            <div className='tfa-confirm-code-panel-inner-container'>
              <div className='form-label'>Verification code</div>
              <div className='tfa-confirm-code-and-note-container'>
                <div className='tfa-confirm-code-grid'>
                  {
                    this.state.code.map((value, index) => (
                      <input
                        autoComplete='one-time-code'
                        className='tfa-confirm-code-input'
                        id={`tfa-confirm-code-input-${index}`}
                        inputMode='numeric'
                        key={index}
                        maxLength={1}
                        onChange={event => this.handleCodeChange(index, event)}
                        onKeyDown={event => this.handleKeyDown(index, event)}
                        ref={element => this.setInputRef(index, element)}
                        type='text'
                        value={value}
                      />
                    ))
                  }
                </div>
                {this.props.enrollmentMode === 'app' &&
                <Text className='tfa-confirm-code-note'>
                  Code refreshes every 30 seconds
                </Text>}
                {error && <div className='error tfa-confirm-error'>{error}</div>}
              </div>
            </div>
          </div>
          <div className='tfa-confirm-help'>
            <ErrorIcon className='help-icon' color='inherit' fontSize='inherit' />
            <div className='tfa-confirm-help-text'>
              {
                this.props.enrollmentMode === 'phone' ?
                  <span>Haven't received your code yet? </span> :
                  <strong>Code not working? </strong>
              }
              {
                this.props.enrollmentMode === 'phone' ?
                  <button className='tfa-inline-link' onClick={this.props.restartFunction} type='button'>Resend code</button> :
                  <span>Make sure your device time is set to automatic. Authenticator apps rely on accurate time to generate valid codes.</span>
              }
            </div>
          </div>
          {
            this.props.phoneVerified &&
              <div className='tfa-confirm-success'>
                <CheckCircleOutlineIcon fontSize='inherit' />
                <span>{this.props.enrollmentMode === 'phone' ? 'Phone number' : 'Authenticator app'} verified</span>
              </div>
          }
          <input type='submit' hidden />
        </form>
      </TfaWizardStepFrame>
    )
  }
}

ConfirmCodeForm.propTypes = {
  confirmCode: PropTypes.func.isRequired,
  enrollmentMode: PropTypes.string,
  error: PropTypes.string,
  phoneVerified: PropTypes.bool,
  phoneNumber: PropTypes.object.isRequired,
  restartFunction: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  verifyEnrollment: PropTypes.func
}

export default ConfirmCodeForm
